import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { Siren } from 'lucide-react';
import { DataTable, DateField, EmptyState, ErrorState, FormField, PageHeader, SearchPanel, StatusBadge, TextField, type Column } from '@/components';
import { useFiscalYear } from '@/fiscal-year/FiscalYearProvider';
import { BusinessOperatorSelect } from '@/features/masters';
import { BulkReportButton } from '@/features/report-jobs/BulkReportButton';
import { formatNumber } from '@/lib/format';
import { weekdayJa } from '@/lib/wareki';
import { CATCH_LABELS, PAGE_SIZE, useVoyages, type VoyageListFilter, type VoyageListItem } from './api';
import '@/features/advance-notices/advance-notices.css';

const KEYS = ['businessOperatorId', 'vesselName', 'registrationNo', 'surveyFrom', 'surveyTo', 'page'] as const;

function useFilterFromUrl(): [VoyageListFilter, (f: VoyageListFilter) => void] {
  const [params, setParams] = useSearchParams();
  const filter = useMemo<VoyageListFilter>(() => {
    const f: Record<string, string | number> = {};
    KEYS.forEach((k) => {
      const v = params.get(k);
      if (v) f[k] = k === 'businessOperatorId' || k === 'page' ? Number(v) : v;
    });
    return f as VoyageListFilter;
  }, [params]);
  const setFilter = (f: VoyageListFilter) => {
    const next = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => v !== '' && v != null && next.set(k, String(v)));
    setParams(next, { replace: true });
  };
  return [filter, setFilter];
}

const md = (d: string) => `${d.slice(5).replace('-', '/')}（${weekdayJa(d)}）`;

/** S-40 速報一覧（速報検索・編集） */
export function VoyageListPage() {
  const navigate = useNavigate();
  const { year, yearPath } = useFiscalYear();
  const [filter, setFilter] = useFilterFromUrl();
  const { data, isPending, isFetching, error, refetch } = useVoyages(year, filter);
  const { register, handleSubmit, reset, control } = useForm<VoyageListFilter>({ values: filter });

  const columns: Column<VoyageListItem>[] = [
    { key: 'businessOperatorName', header: '事業実施者', width: 170 },
    {
      key: 'vesselName',
      header: '船名（カナ）',
      render: (r) => (
        <>
          {r.vesselName}
          <span className="bs-cell-sub">{r.vesselNameKana}</span>
        </>
      ),
    },
    { key: 'registrationNo', header: '漁船登録番号', width: 130, render: (r) => <span className="bs-num">{r.registrationNo}</span> },
    { key: 'tonnage', header: 'トン数', numeric: true, width: 80, render: (r) => formatNumber(r.tonnage, 2) },
    { key: 'captainName', header: '船長', width: 120 },
    { key: 'periodFrom', header: '調査期間', width: 220, render: (r) => <span className="bs-num">{md(r.periodFrom)} → {md(r.periodTo)}</span> },
    { key: 'actualWorkDays', header: '実作業日数', numeric: true, width: 100, render: (r) => `${formatNumber(r.actualWorkDays, 2)}日` },
    {
      key: 'catchSurveyStatus',
      header: '漁獲調査等',
      width: 110,
      render: (r) => (r.catchSurveyStatus ? <StatusBadge tone="info" title={CATCH_LABELS[r.catchSurveyStatus]}>{r.catchSurveyStatus}</StatusBadge> : <StatusBadge tone="warning">未入力</StatusBadge>),
    },
  ];
  return (
    <>
      <PageHeader
        eyebrow="日々の報告"
        title="速報"
        description="外国漁船操業等調査・監視報告書（速報 別記様式第2-2号）の検索・編集。新規登録は「事前連絡」一覧の各行から行います。"
        actions={<BulkReportButton reportId="R-04" params={Object.fromEntries(Object.entries({ ...filter, page: undefined }).filter(([, v]) => v !== '' && v != null))} disabled={!data?.total} navigateAfter />}
      />
      <SearchPanel
        onSubmit={handleSubmit((v) => setFilter({ ...v, page: 1 }))}
        onReset={() => {
          reset({});
          setFilter({});
        }}
        searching={isFetching}
        columns={5}
      >
        <FormField label="事業実施者">
          <Controller
            control={control}
            name="businessOperatorId"
            render={({ field }) => <BusinessOperatorSelect fiscalYear={year} value={field.value ? Number(field.value) : null} onChange={(v) => field.onChange(v ?? '')} width="full" />}
          />
        </FormField>
        <FormField label="船名" hint="船名・カナの部分一致">
          <TextField {...register('vesselName')} />
        </FormField>
        <FormField label="漁船登録番号">
          <TextField {...register('registrationNo')} />
        </FormField>
        <FormField label="調査日（から）">
          <DateField {...register('surveyFrom')} />
        </FormField>
        <FormField label="調査日（まで）">
          <DateField {...register('surveyTo')} />
        </FormField>
      </SearchPanel>
      {error ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : (
        <DataTable
          caption="速報一覧"
          columns={columns}
          rows={data?.items ?? []}
          rowKey={(r) => r.id}
          loading={isPending}
          pagination={{ pageSize: PAGE_SIZE, page: filter.page ?? 1, total: data?.total ?? 0, onPageChange: (p) => setFilter({ ...filter, page: p }) }}
          onRowClick={(r) => navigate(yearPath(`voyage-reports/${r.id}`))}
          empty={<EmptyState compact icon={<Siren />} title="該当する速報はありません" description="検索条件を変更してください。" />}
        />
      )}
    </>
  );
}
